import React, {useEffect} from "react";
import '../../styles/components/lesson/lesson-page.scss';
import ControlBar from "./ControlBar";
import Segment from "./Segment";
import "../../services/playbackOrchestrator/playbackOrchestrator";
import ScoreModal from "./score/ScoreModal";
import SectionEndModal from "./sectionEndModal/SectionEndModal";
import SectionStartModal from "./sectionStartModal/SectionStartModal";
import LessonEndsWithVideoModal from "./lessonEndsWithVideoModal/LessonEndsWithVideoModal";
import NewSectionModal from "./newSectionModal/NewSectionModal";
import {setProgressBarVisibility} from "./progressBar/ProgressBarLogic";
import {TranscriptionProgressModal} from "./transcriptionProgressModal/TranscriptionProgressModal";
import {useAppSelector} from "../../redux/hooks";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import KeyboardDisconnectedModal from "./keyboardDisconnectedModal/KeyboardDisconnectedModal";
import {GenericModal} from "../common/genericModal/GenericModal";
import {settingsStore} from "../../redux/settings/settingsStore";
import PitchDetectionDebugging from "./PitchDetectionDebugging";
import ScoringAccuracyTipsModal from "./scoringAccuracyTips/ScoringAccuracyTipsModal";
import SoundDemoPrompt from "./soundDemoPrompt/SoundDemoPrompt";
import {useLessonPage} from "./LessonPage.hook";
import {MicrophoneAccessModal} from "./microphone/MicrophoneAccessModal";
import {default as SegmentType} from "../../models/view/lessonPage/Segment";
import KeyboardWizardModal from "../onboarding/keyboardWizard/KeyboardWizardModal";
import SoundDemosActivatedPrompt from "./soundDemosActivatedPrompt/SoundDemosActivatedPrompt";
import SwitchToPitchDetectionPrompt from "./switchToPitchDetectionPrompt/SwitchToPitchDetectionPrompt";
import SilentModeWarning from "../common/SilentModeWarning";

/**
 * We can remove the unused component properties when we're sure they're not needed for other purposes.
 */
export default function LessonPage() {
  const canShowKeyboardConnectedModal = useAppSelector(lessonPlayerStore.getCanShowKeyboardConnectedModal);
  const selectedDeviceName = useAppSelector(settingsStore.getSelectedMidiDeviceName);
  const segment = useAppSelector(lessonPlayerStore.getSegment);
  const isFirstSegmentOfLesson = segment?.isFirstSegmentOfLesson ?? false;
  const isLastSegmentOfLesson = segment?.isLastSegmentOfLesson ?? false;
  const isFirstLessonOfSection = useAppSelector(lessonPlayerStore.getIsFirstLessonOfSection);
  const lesson = useAppSelector(lessonPlayerStore.getLesson);
  const section = lesson?.section;
  const nextSection = lesson?.nextSection;
  const sectionNumber = section?.displayNumber ?? 0;
  const sectionName = section?.title ?? "";
  const sectionDescription = section?.description ?? "";
  let nextLessonId: number | undefined = Number(lesson?.nextLesson?.id);
  nextLessonId = isNaN(nextLessonId) ? undefined : nextLessonId;
  const isLastLessonOfSection = lesson?.isLastLessonOfSection ?? false;
  const activeLessonId = useAppSelector(lessonPlayerStore.getActiveLessonId);
  const {
    isPitchDetectionEnabled,
    scoreModalProps,
    scoringAccuracyTipsModalProps,
    microphoneAccessModalProps,
    onPlayStopPauseButton_clicked,
  } = useLessonPage();

  /**
   * This `useEffect` is in place to combat an error in the console.
   * Its efficacy is uncertain, since AI suggested its use, but without a comprehensible explanation of the root cause.
   * Testing has so far indicated that the error is not present when this `useEffect` is in place,
   * but this is considered inconclusive to-date.
   */
  useEffect(() => {
    // Need to load proportional notation spacing for the lesson from local storage if we have it.
    tryToSetActiveLessonAndProportionalNotationSpacing(segment, activeLessonId);
  }, [segment, activeLessonId]);

  // Auto-dismiss the "Keyboard Connected" modal after 2 seconds.
  useEffect(() => {
    if (canShowKeyboardConnectedModal) {
      const timeoutId = setTimeout(() => {
        lessonPlayerStore.setCanShowKeyboardConnectedModal(false);
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [canShowKeyboardConnectedModal]);

  const isFirstSegmentOfFirstLessonOfSection = isFirstLessonOfSection && isFirstSegmentOfLesson;

  if (isFirstSegmentOfFirstLessonOfSection && section) {
    lessonPlayerStore.setDisplaySectionStartModal(true);
  }

  return (
    <div className="lesson-page" onMouseOver={() => {
      setProgressBarVisibility();
    }}>
      <PitchDetectionDebugging isPitchDetectionEnabled={isPitchDetectionEnabled} />

      <MicrophoneAccessModal {...microphoneAccessModalProps} />
      <ControlBar onPlayStopPauseButton_clicked={onPlayStopPauseButton_clicked} canMicrophoneAccessModalBeShown={microphoneAccessModalProps.canBeShown}/>
      <Segment/>
      <NewSectionModal sectionNumber={sectionNumber} sectionName={sectionName} sectionDescription={sectionDescription}/>
      <SectionStartModal
        sectionNumber={section?.displayNumber ?? 0}
        sectionTitle={section?.title ?? ""}
        sectionDescription={section?.description ?? ""}
      />
      <ScoreModal {...scoreModalProps} />
      <LessonEndsWithVideoModal
        nextLessonId={nextLessonId}
        isLastSegmentOfLesson={isLastSegmentOfLesson}
        isLastLessonOfSection={isLastLessonOfSection}
      />
      <SectionEndModal
        section={section}
        nextSection={nextSection}
        onReplayButtonClick={onPlayStopPauseButton_clicked}
      />
      <TranscriptionProgressModal/>
      <KeyboardWizardModal onStartPlaying={() => {}} />
      <KeyboardDisconnectedModal />
      <ScoringAccuracyTipsModal {...scoringAccuracyTipsModalProps} />
      {renderKeyboardConnectedPopup(canShowKeyboardConnectedModal, selectedDeviceName)}
      <SoundDemoPrompt />
      <SoundDemosActivatedPrompt />
      <SwitchToPitchDetectionPrompt />
      <SilentModeWarning/>
    </div>
  );
}

const renderKeyboardConnectedPopup = (
  canShowKeyboardConnectedModal: boolean,
  selectedDeviceName: string) => {
  return (
    <GenericModal
      canShow={(): boolean => {
        return canShowKeyboardConnectedModal && selectedDeviceName.length > 0;
      }}
      backgroundColor={"white"}
      title={"Keyboard connected"}
      maxWidth={"350px"}
      onClose={() => {
        lessonPlayerStore.setCanShowKeyboardConnectedModal(false);
      }}
    >
      {selectedDeviceName} connected
    </GenericModal>
  );
}

function tryToSetActiveLessonAndProportionalNotationSpacing(segment: SegmentType | undefined, activeLessonId: number | undefined ): void {
  if (segment) {
    const id = parseInt(segment.lessonId);
    if (id !== activeLessonId && !isNaN(id)) {
      lessonPlayerStore.setActiveLessonId(id);
      const lessonSpacing = lessonPlayerStore.getAllLessonSpacing();
      const spacing = lessonSpacing.find((spacing) => spacing.lessonId === id)?.spacing;
      if (spacing) {
        lessonPlayerStore.setSpacingForActiveLesson(spacing);
      }
    }
  } else {
    lessonPlayerStore.setActiveLessonId(undefined);
    lessonPlayerStore.setSpacingForActiveLesson(undefined);
  }
}
